import { TripBreakdownsOrderBy } from 'common/types/globalTypes';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { FilterArrayType } from 'common/components/Filters';

type OrderLookup = {
  [direction in OrderDirection]: TripBreakdownsOrderBy[];
};

const orderByMap = new Map<string, OrderLookup>([
  [
    'tripLocator',
    {
      [OrderDirection.ASC]: ['TRIP_LOCATOR_ASC'],
      [OrderDirection.DESC]: ['TRIP_LOCATOR_DESC'],
    },
  ],
  [
    '_travelTags',
    {
      [OrderDirection.ASC]: ['_TRAVEL_TAGS_ASC'],
      [OrderDirection.DESC]: ['_TRAVEL_TAGS_DESC'],
    },
  ],
  [
    'travelerReferenceName',
    {
      [OrderDirection.ASC]: ['TRAVELER_REFERENCE_NAME_ASC'],
      [OrderDirection.DESC]: ['TRAVELER_REFERENCE_NAME_DESC'],
    },
  ],
  [
    'travelAuthorizationNumber',
    {
      [OrderDirection.ASC]: ['TRAVEL_AUTHORIZATION_NUMBER_ASC'],
      [OrderDirection.DESC]: ['TRAVEL_AUTHORIZATION_NUMBER_DESC'],
    },
  ],
  [
    '_isCorporateTravelAuthorization',
    {
      [OrderDirection.ASC]: ['_IS_CORPORATE_TRAVEL_AUTHORIZATION_ASC'],
      [OrderDirection.DESC]: ['_IS_CORPORATE_TRAVEL_AUTHORIZATION_DESC'],
    },
  ],
  [
    'comment',
    {
      [OrderDirection.ASC]: ['COMMENT_ASC'],
      [OrderDirection.DESC]: ['COMMENT_DESC'],
    },
  ],
  [
    'travelAuthorizationDate',
    {
      [OrderDirection.ASC]: ['TRAVEL_AUTHORIZATION_DATE_ASC'],
      [OrderDirection.DESC]: ['TRAVEL_AUTHORIZATION_DATE_DESC'],
    },
  ],
  [
    '_departmentName',
    {
      [OrderDirection.ASC]: ['_DEPARTMENT_NAME_ASC'],
      [OrderDirection.DESC]: ['_DEPARTMENT_NAME_DESC'],
    },
  ],
  [
    '_businessUnitName',
    {
      [OrderDirection.ASC]: ['_BUSINESS_UNIT_NAME_ASC'],
      [OrderDirection.DESC]: ['_BUSINESS_UNIT_NAME_DESC'],
    },
  ],
  [
    'corporateWorkgroupName',
    {
      [OrderDirection.ASC]: ['CORPORATE_WORKGROUP_NAME_ASC'],
      [OrderDirection.DESC]: ['CORPORATE_WORKGROUP_NAME_DESC'],
    },
  ],
  [
    'tripPurpose',
    {
      [OrderDirection.ASC]: ['TRIP_PURPOSE_ASC'],
      [OrderDirection.DESC]: ['TRIP_PURPOSE_DESC'],
    },
  ],
  [
    '_travelPolicyDescription',
    {
      [OrderDirection.ASC]: ['_TRAVEL_POLICY_DESCRIPTION_ASC'],
      [OrderDirection.DESC]: ['_TRAVEL_POLICY_DESCRIPTION_DESC'],
    },
  ],
  [
    '_companionTicketUsed',
    {
      [OrderDirection.ASC]: ['_COMPANION_TICKET_USED_ASC'],
      [OrderDirection.DESC]: ['_COMPANION_TICKET_USED_DESC'],
    },
  ],
  [
    '_isoCode',
    {
      [OrderDirection.ASC]: ['_ISO_CODE_ASC'],
      [OrderDirection.DESC]: ['_ISO_CODE_DESC'],
    },
  ],
  [
    'airfareAmount',
    {
      [OrderDirection.ASC]: ['AIRFARE_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['AIRFARE_AMOUNT_DESC'],
    },
  ],
  [
    'accommodationAmount',
    {
      [OrderDirection.ASC]: ['ACCOMMODATION_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['ACCOMMODATION_AMOUNT_DESC'],
    },
  ],
  [
    'vehicleRentalAmount',
    {
      [OrderDirection.ASC]: ['VEHICLE_RENTAL_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['VEHICLE_RENTAL_AMOUNT_DESC'],
    },
  ],
  [
    'transportationAmount',
    {
      [OrderDirection.ASC]: ['TRANSPORTATION_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['TRANSPORTATION_AMOUNT_DESC'],
    },
  ],
  [
    'trainAmount',
    {
      [OrderDirection.ASC]: ['TRAIN_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['TRAIN_AMOUNT_DESC'],
    },
  ],
  [
    'incidentalAmount',
    {
      [OrderDirection.ASC]: ['INCIDENTAL_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['INCIDENTAL_AMOUNT_DESC'],
    },
  ],
  [
    'controlTotalAmount',
    {
      [OrderDirection.ASC]: ['CONTROL_TOTAL_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['CONTROL_TOTAL_AMOUNT_DESC'],
    },
  ],
  [
    '_baseCurrencyAmount',
    {
      [OrderDirection.ASC]: ['_BASE_CURRENCY_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['_BASE_CURRENCY_AMOUNT_DESC'],
    },
  ],
  [
    '_spotCurrencyAmount',
    {
      [OrderDirection.ASC]: ['_SPOT_CURRENCY_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['_SPOT_CURRENCY_AMOUNT_DESC'],
    },
  ],
  [
    '_taStatusType',
    {
      [OrderDirection.ASC]: ['_TA_STATUS_TYPE_ASC'],
      [OrderDirection.DESC]: ['_TA_STATUS_TYPE_DESC'],
    },
  ],
  [
    '_createdDate',
    {
      [OrderDirection.ASC]: ['_CREATED_DATE_ASC'],
      [OrderDirection.DESC]: ['_CREATED_DATE_DESC'],
    },
  ],
  [
    '_createdByUserName',
    {
      [OrderDirection.ASC]: ['_CREATED_BY_USER_NAME_ASC'],
      [OrderDirection.DESC]: ['_CREATED_BY_USER_NAME_DESC'],
    },
  ],
  [
    '_companionAmountUsed',
    {
      [OrderDirection.ASC]: ['_COMPANION_AMOUNT_USED_ASC'],
      [OrderDirection.DESC]: ['_COMPANION_AMOUNT_USED_DESC'],
    },
  ],
  [
    '_tripWarningInfo',
    {
      [OrderDirection.ASC]: ['_TRIP_WARNING_INFO_ASC'],
      [OrderDirection.DESC]: ['_TRIP_WARNING_INFO_DESC'],
    },
  ],
  [
    '_primaryTravelerName',
    {
      [OrderDirection.ASC]: ['_PRIMARY_TRAVELER_NAME_ASC'],
      [OrderDirection.DESC]: ['_PRIMARY_TRAVELER_NAME_DESC'],
    },
  ],
]);

export const toOrderByVariable = (
  sort?: SortOrder
): TripBreakdownsOrderBy[] => {
  const orderByArray: TripBreakdownsOrderBy[] = [
    'TRAVEL_AUTHORIZATION_NUMBER_DESC',
    'PRIMARY_KEY_ASC',
  ];

  if (!sort) return orderByArray;

  const orderBy = orderByMap.get(sort.column)?.[sort.direction];

  return [
    ...(orderBy ? (Array.isArray(orderBy) ? orderBy : [orderBy]) : []),
    'PRIMARY_KEY_ASC',
  ];
};

export const toTripBreakdownFilterVariable = (filterList: FilterArrayType[]) =>
  filterList.map((filter) => {
    switch (filter.filterKey) {
      case 'departmentId':
      case 'currencyId':
      case 'businessUnitId':
      case '_taStatusTypeId':
      case 'travelerId':
      case 'travelAuthorizationId':
      case 'corporateWorkgroupId':
      case '_travelPolicyId':
        return {
          [filter.filterKey]: {
            equalTo: filter.value,
          },
        };
      case '_isOverBudget':
        return {
          _isOverBudget: {
            equalTo: filter.value === 1,
          },
        };

      case '_isCorporateTravelAuthorization':
        return {
          _isCorporateTravelAuthorization: {
            equalTo: Boolean(filter.value),
          },
        };
      default:
        return undefined;
    }
  });
